import { supabase } from '@/lib/supabase/client';
import {
  canonicalLeadIds,
  parseOutreachSafetySummarySuccess,
} from '@/lib/outreach-safety/summary-contract';
import type { WeekQueueRankingLead } from '@/lib/outreach/week-queue-ranking';
import type {
  IncumbentInstoreTech,
  LeadOutreachSafetyMatchSummary,
  LeadOutreachSafetySummary,
  Lead,
  LeadListRow,
  LeadSegment,
  LeadStatus,
  LeadUpdate,
  PriorityBand,
  TechPosture,
} from '@/types/leads';

// Reads discovered-dispensary candidate leads written by the tpb-dispensary-leadgen skill.
// Same `leads` table the skill upserts into (migration 064) — page and skill share the contract.

const leadSegments = ['single_location', 'small_chain', 'regional_chain', 'mso'] as const;
const priorityBands = ['priority', 'review', 'hold', 'recheck', 'suppressed'] as const;
const techPostures = ['tech_forward', 'tech_curious', 'traditional', 'tech_resistant'] as const;
const leadStatuses = [
  'discovered',
  'enriched',
  'scored',
  'qa_review',
  'needs_review',
  'recheck',
  'human_review',
  'qualified',
  'converted',
  'needs_more_research',
  'suppressed',
  'do_not_contact',
  'disqualified',
] as const;
const scoreConfidences = ['high', 'medium', 'low'] as const;
const suppressionReasons = [
  'do_not_contact',
  'unsubscribe',
  'hard_bounce',
  'spam_complaint',
  'invalid_email',
  'current_customer',
  'existing_customer',
  'open_deal',
  'inbound_dup',
  'manual_block',
  'other',
] as const;
const matchedEntityTypes = ['customer_location', 'open_deal', 'blueprint_lead', 'existing_lead'] as const;
const SAFETY_SUMMARY_QUERY_CHUNK_SIZE = 100;

interface LeadListOptions {
  includeSuppressed?: boolean;
  includeDisqualified?: boolean;
  includeArchived?: boolean;
}

function stringOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

function numberOrNull(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function enumOrNull<T extends string>(value: unknown, allowed: readonly T[]): T | null {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value) ? (value as T) : null;
}

function stringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
}

function booleanOrNull(value: unknown): boolean | null {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true') return true;
    if (normalized === 'false') return false;
  }
  if (typeof value === 'number') {
    if (value === 1) return true;
    if (value === 0) return false;
  }
  return null;
}

function asRedactedLead(row: Record<string, unknown>): Lead {
  return { ...row, is_redacted: true } as unknown as Lead;
}

async function listRedactedLeads(options: {
  leadIds?: string[];
  includeSuppressed?: boolean;
  includeDisqualified?: boolean;
  includeArchived?: boolean;
  limit?: number;
} = {}): Promise<Record<string, unknown>[]> {
  const { data, error } = await supabase.rpc('list_leads_redacted', {
    p_lead_ids: options.leadIds?.length ? options.leadIds : null,
    p_include_suppressed: options.includeSuppressed ?? true,
    p_include_disqualified: options.includeDisqualified ?? false,
    p_limit: options.limit ?? 1000,
    p_include_archived: options.includeArchived ?? false,
  });
  if (error) throw new Error(`Failed to fetch leads: ${error.message}`);
  return (data as Record<string, unknown>[] | null) ?? [];
}

function normalizeIncumbentInstoreTech(value: unknown): IncumbentInstoreTech | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;

  const record = value as Record<string, unknown>;
  const vendor = stringOrNull(record.vendor);
  const evidence = stringOrNull(record.evidence);

  // New evidence-graded shape: explicit status.
  const rawStatus = stringOrNull(record.status);
  if (rawStatus === 'competitor' || rawStatus === 'not_confirmed' || rawStatus === 'greenfield') {
    return { status: rawStatus, vendor, evidence };
  }

  // Legacy shape { present } → map honestly: present:false is NOT a confirmed greenfield.
  const present = booleanOrNull(record.present);
  if (present === null && vendor === null) return null;
  return { status: present === true ? 'competitor' : 'not_confirmed', vendor, evidence };
}

function normalizeLeadListRow(
  row: Record<string, unknown>,
  outreachSuppression: LeadOutreachSafetySummary | null = null,
  outreachSafetyMatch: LeadOutreachSafetyMatchSummary | null = null
): LeadListRow {
  return {
    id: stringOrNull(row.id) ?? '',
    business_name: stringOrNull(row.business_name) ?? 'Unnamed lead',
    dba: stringOrNull(row.dba),
    city: stringOrNull(row.city),
    state: stringOrNull(row.state),
    pos_vendor: stringOrNull(row.pos_vendor),
    menu_vendor: stringOrNull(row.menu_vendor),
    segment: enumOrNull<LeadSegment>(row.segment, leadSegments),
    location_count: numberOrNull(row.location_count),
    operator_id: stringOrNull(row.operator_id),
    priority_band: enumOrNull<PriorityBand>(row.priority_band, priorityBands),
    lead_score: numberOrNull(row.lead_score),
    need_score: numberOrNull(row.need_score),
    winnability_score: numberOrNull(row.winnability_score),
    value_score: numberOrNull(row.value_score),
    score_confidence: enumOrNull(row.score_confidence, scoreConfidences),
    nearest_tpb_distance_mi: numberOrNull(row.nearest_tpb_distance_mi),
    nearest_tpb_location_name: stringOrNull(row.nearest_tpb_location_name),
    incumbent_instore_tech: normalizeIncumbentInstoreTech(row.incumbent_instore_tech),
    solvable_pain_points: stringArray(row.solvable_pain_points),
    tech_posture: enumOrNull<TechPosture>(row.tech_posture, techPostures),
    status: enumOrNull<LeadStatus>(row.status, leadStatuses),
    match_status: outreachSafetyMatch?.match_status ?? stringOrNull(row.match_status),
    suppression_reason: outreachSafetyMatch?.suppression_reason ?? enumOrNull(row.suppression_reason, suppressionReasons),
    matched_entity_type: outreachSafetyMatch?.matched_entity_type ?? enumOrNull(row.matched_entity_type, matchedEntityTypes),
    matched_entity_id: outreachSafetyMatch?.matched_entity_id ?? stringOrNull(row.matched_entity_id),
    do_not_contact: outreachSafetyMatch?.do_not_contact ?? booleanOrNull(row.do_not_contact) ?? false,
    contact_readiness_status:
      row.contact_readiness_status === 'ready' || row.contact_readiness_status === 'needs_contact'
        ? row.contact_readiness_status
        : 'unknown',
    contact_readiness_reason:
      typeof row.contact_readiness_reason === 'string'
        ? (row.contact_readiness_reason as LeadListRow['contact_readiness_reason'])
        : 'legacy_unclassified',
    operator_link_method:
      typeof row.operator_link_method === 'string'
        ? (row.operator_link_method as LeadListRow['operator_link_method'])
        : 'legacy_unverified',
    archived_at: stringOrNull(row.archived_at),
    archived_reason: stringOrNull(row.archived_reason),
    revived_at: stringOrNull(row.revived_at),
    created_at: stringOrNull(row.created_at) ?? '',
    updated_at: stringOrNull(row.updated_at) ?? '',
    outreach_suppression: outreachSuppression,
  };
}

function chunkValues<T>(values: T[], chunkSize: number): T[][] {
  const chunks: T[][] = [];
  for (let index = 0; index < values.length; index += chunkSize) {
    chunks.push(values.slice(index, index + chunkSize));
  }
  return chunks;
}

async function loadOutreachSafetyContext(leadIds: string[]): Promise<{
  suppressionSummaries: Map<string, LeadOutreachSafetySummary>;
  matchSummaries: Map<string, LeadOutreachSafetyMatchSummary>;
}> {
  if (leadIds.length === 0) {
    return { suppressionSummaries: new Map(), matchSummaries: new Map() };
  }

  const suppressionData: LeadOutreachSafetySummary[] = [];
  const matchData: LeadOutreachSafetyMatchSummary[] = [];
  const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
  const accessToken = sessionData.session?.access_token;
  if (sessionError || !accessToken) {
    throw new Error('Outreach safety summary unavailable: sign in again.');
  }

  for (const leadIdChunk of chunkValues(canonicalLeadIds(leadIds), SAFETY_SUMMARY_QUERY_CHUNK_SIZE)) {
    const requestedLeadIds = canonicalLeadIds(leadIdChunk);
    const response = await fetch('/api/outreach-safety-summaries', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ leadIds: requestedLeadIds }),
    });
    const payload = await response.json().catch(() => null);

    if (!response.ok) {
      throw new Error(
        payload?.error?.message || `Outreach safety summary unavailable (${response.status}).`,
      );
    }

    const parsed = parseOutreachSafetySummarySuccess(payload, requestedLeadIds);
    suppressionData.push(...parsed.summaries);
    matchData.push(...parsed.matchSummaries);
  }

  return {
    suppressionSummaries: new Map(suppressionData.map((summary) => [summary.lead_id, summary])),
    matchSummaries: new Map(matchData.map((summary) => [summary.lead_id, summary])),
  };
}

export const leadsService = {
  async listRedactedRecords(options: LeadListOptions = {}): Promise<Record<string, unknown>[]> {
    return listRedactedLeads({
      includeSuppressed: options.includeSuppressed,
      includeDisqualified: options.includeDisqualified,
      includeArchived: options.includeArchived,
    });
  },

  // Defaults to active, non-archived prospects only. Safety-audit views can opt into
  // suppressed/do-not-contact rows so the admin can see why a lead is blocked before any
  // outreach export happens; the Archived tab opts into archived rows the same way.
  async getAll(options: LeadListOptions = {}): Promise<LeadListRow[]> {
    const rows = await listRedactedLeads({
      includeSuppressed: options.includeSuppressed,
      includeDisqualified: options.includeDisqualified,
      includeArchived: options.includeArchived,
    });
    const leadIds = rows.map((row) => stringOrNull(row.id)).filter(Boolean) as string[];
    const { suppressionSummaries, matchSummaries } = await loadOutreachSafetyContext(leadIds);
    return rows.map((row) => {
      const leadId = stringOrNull(row.id) ?? '';
      return normalizeLeadListRow(
        row,
        suppressionSummaries.get(leadId) ?? null,
        matchSummaries.get(leadId) ?? null
      );
    });
  },

  async getOutreachRankingCandidates(): Promise<WeekQueueRankingLead[]> {
    // The read API deliberately excludes delivery identifiers. Queue ranking therefore cannot
    // produce sendable candidates until a separately reviewed delivery release is introduced.
    const rows = await listRedactedLeads({ includeSuppressed: false, includeDisqualified: false });
    const leadIds = rows.map((row) => stringOrNull(row.id)).filter(Boolean) as string[];
    const { suppressionSummaries, matchSummaries } = await loadOutreachSafetyContext(leadIds);
    const { data: readinessRows, error: readinessError } = await supabase.rpc(
      'get_lead_week_queue_readiness',
      { p_lead_ids: leadIds }
    );
    if (readinessError) {
      throw new Error(`Week queue readiness unavailable: ${readinessError.message}`);
    }
    const readyLeadIds = new Set(
      ((readinessRows as Array<{ lead_id: string; state: string }> | null) ?? [])
        .filter((row) => row.state === 'ready')
        .map((row) => row.lead_id)
    );

    const results: WeekQueueRankingLead[] = [];

    for (const row of rows) {
      const leadId = stringOrNull(row.id);
      if (!leadId) continue;
      if (!readyLeadIds.has(leadId)) continue;
      const matchSummary = matchSummaries.get(leadId);
      results.push({
        id: leadId,
        business_name: stringOrNull(row.business_name) ?? '',
        dba: stringOrNull(row.dba),
        city: stringOrNull(row.city),
        state: stringOrNull(row.state),
        email: stringOrNull(row.email),
        contact_person: stringOrNull(row.contact_person),
        contact_resolved_at: stringOrNull(row.contact_resolved_at),
        lead_score: numberOrNull(row.lead_score),
        need_score: numberOrNull(row.need_score),
        winnability_score: numberOrNull(row.winnability_score),
        value_score: numberOrNull(row.value_score),
        priority_band: enumOrNull(row.priority_band, priorityBands),
        solvable_pain_points: stringArray(row.solvable_pain_points),
        matched_proof_metric_ids: stringArray(row.matched_proof_metric_ids),
        status: enumOrNull(row.status, leadStatuses) ?? 'discovered',
        do_not_contact: booleanOrNull(row.do_not_contact) ?? false,
        match_status: matchSummary?.match_status ?? stringOrNull(row.match_status),
        suppression_reason: enumOrNull(row.suppression_reason, suppressionReasons),
        matched_entity_type:
          matchSummary?.matched_entity_type ?? enumOrNull(row.matched_entity_type, matchedEntityTypes),
        matched_entity_id: matchSummary?.matched_entity_id ?? stringOrNull(row.matched_entity_id),
        operator_id: stringOrNull(row.operator_id),
        segment: enumOrNull(row.segment, leadSegments),
        location_count: numberOrNull(row.location_count),
        contact_readiness_status:
          row.contact_readiness_status === 'ready' || row.contact_readiness_status === 'needs_contact'
            ? row.contact_readiness_status
            : 'unknown',
        operator_link_method:
          typeof row.operator_link_method === 'string'
            ? (row.operator_link_method as WeekQueueRankingLead['operator_link_method'])
            : 'legacy_unverified',
        outreach_suppression: suppressionSummaries.get(leadId) ?? null,
      });
    }

    return results;
  },

  // Full row incl. contact details — for an intentional detail view (not the list).
  async getById(id: string): Promise<Lead | null> {
    const { data, error } = await supabase.rpc('get_lead_detail_redacted', { p_lead_id: id });
    if (error) throw new Error(`Failed to fetch lead: ${error.message}`);
    return data ? asRedactedLead(data as Record<string, unknown>) : null;
  },

  async getByIds(ids: string[]): Promise<Lead[]> {
    if (ids.length === 0) return [];

    const uniqueIds = [...new Set(ids)];
    const chunkSize = 100;
    const leads: Lead[] = [];

    for (let index = 0; index < uniqueIds.length; index += chunkSize) {
      const chunk = uniqueIds.slice(index, index + chunkSize);
      const rows = await listRedactedLeads({
        leadIds: chunk,
        includeSuppressed: true,
        includeDisqualified: true,
        includeArchived: true, // lookup by known id must never silently drop an archived row
      });
      leads.push(...rows.map(asRedactedLead));
    }

    const byId = new Map(leads.map((lead) => [lead.id, lead]));
    return ids.map((id) => byId.get(id)).filter((lead): lead is Lead => Boolean(lead));
  },

  async findByEmails(emails: string[]): Promise<Lead[]> {
    const normalized = [...new Set(emails.map((email) => email.trim().toLowerCase()).filter(Boolean))];
    if (normalized.length === 0) return [];

    const { data, error } = await supabase.rpc('find_leads_redacted', {
      p_emails: normalized,
      p_store_names: null,
      p_limit: 500,
    });
    if (error) throw new Error(`Failed to fetch leads by email: ${error.message}`);
    return ((data as Record<string, unknown>[] | null) ?? []).map(asRedactedLead);
  },

  /**
   * Active Prospecting rows with email — used for nurture/duplicate reconcile.
   * Currently an inert stub (the whole reconcile pipeline is unimplemented — see
   * lead-lists.ts's applyReconcileDisqualifyActions). Whoever wires this up must call
   * listRedactedLeads/getAll without includeArchived so archived leads are excluded
   * by default, same as every other active-prospect read path.
   */
  async listReconcileCandidates(): Promise<Lead[]> {
    return [];
  },

  /**
   * Active leads that lack a named contact (blank / generic / role inbox).
   * Used to clear exhausted cards from Track A without deleting rows.
   */
  async listNoContactCleanupCandidates(
    excludeLeadIds: string[] = []
  ): Promise<Array<Pick<Lead, 'id' | 'email' | 'contact_person' | 'qa_notes' | 'business_name' | 'dba'>>> {
    void excludeLeadIds;
    // Classification uses delivery identifiers, which are intentionally absent from browser reads.
    return [];
  },

  async findByStoreNames(storeNames: string[]): Promise<Lead[]> {
    const normalized = [...new Set(storeNames.map((name) => name.trim()).filter(Boolean))];
    if (normalized.length === 0) return [];

    const { data, error } = await supabase.rpc('find_leads_redacted', {
      p_emails: null,
      p_store_names: normalized,
      p_limit: 500,
    });
    if (error) throw new Error(`Failed to fetch leads by store name: ${error.message}`);
    return ((data as Record<string, unknown>[] | null) ?? []).map(asRedactedLead);
  },

  async update(id: string, data: LeadUpdate): Promise<Lead> {
    const payload: LeadUpdate = { ...data };
    if (
      'email' in data ||
      'contact_person' in data ||
      'contact_title' in data ||
      'phone' in data
    ) {
      payload.contact_resolved_at = null;
      payload.contact_resolved_by = null;
    }

    const contactKeys = ['email', 'contact_person', 'contact_title', 'phone'] as const;
    const changes: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(payload)) {
      if (value !== undefined && !contactKeys.includes(key as typeof contactKeys[number])) changes[key] = value;
    }
    if (Object.keys(changes).length > 0) {
      const { error } = await supabase.rpc('update_leads_redacted', {
        p_lead_ids: [id], p_changes: changes,
      });
      if (error) throw new Error(`Failed to update lead: ${error.message}`);
    }
    if (contactKeys.some((key) => key in payload)) {
      if (!contactKeys.every((key) => key in payload)) {
        throw new Error('Submit all contact fields together so the governed contact command can verify the revision.');
      }
      const current = await this.getById(id);
      if (!current) throw new Error('Lead not found');
      const { error } = await supabase.rpc('set_lead_contact_readiness_redacted', {
        p_lead_id: id,
        p_contact_person: payload.contact_person ?? null,
        p_contact_title: payload.contact_title ?? null,
        p_email: payload.email ?? null,
        p_phone: payload.phone ?? null,
        p_expected_version: current.contact_readiness_version,
      });
      if (error) throw new Error(`Failed to update lead contact: ${error.message}`);
    }
    const updated = await this.getById(id);
    if (!updated) throw new Error('Lead not found');
    return updated;
  },

  // Archive-in-place / human revival (lead-engine-plan.md §3.7). Dedicated RPCs, not
  // update_leads_redacted — archiving/reviving is a stateful transition (server-stamped
  // timestamp, atomic status change on revive) the generic field-allowlist can't safely express.
  async bulkArchive(ids: string[], reason: string): Promise<number> {
    if (ids.length === 0) return 0;
    const { data, error } = await supabase.rpc('archive_leads_redacted', {
      p_lead_ids: ids,
      p_reason: reason,
    });
    if (error) throw new Error(`Failed to archive leads: ${error.message}`);
    return (data as number | null) ?? 0;
  },

  async bulkRevive(ids: string[]): Promise<number> {
    if (ids.length === 0) return 0;
    const { data, error } = await supabase.rpc('revive_leads_redacted', {
      p_lead_ids: ids,
    });
    if (error) throw new Error(`Failed to revive leads: ${error.message}`);
    return (data as number | null) ?? 0;
  },
};
